import React, { useState, useMemo, useEffect } from "react";
import {
  ComposedChart, BarChart, Bar, Area, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, ReferenceArea
} from "recharts";
 
// ─── MARKET DATA ──────────────────────────────────────────────────────────────
const YEARS = Array.from({length:31},(_,i)=>2020+i);
const FORECAST_START = 2026;
const CHART_START = 2025;
 
const MKT_ANN = {
  low:    [23.73,31.49,44.55,69.94,71.85,64.59,70.84,76.23,80.37,82.93,83.68,82.57,79.69,75.29,69.70,63.33,59.60,52.80,48.75,42.83,37.25,34.02,33.79,30.05,30.52,30.97,28.72,33.79,45.06,43.79,51.84],
  medium: [23.73,31.49,44.55,69.94,71.85,71.96,82.13,92.59,102.92,112.65,121.21,128.07,132.73,134.83,134.22,130.93,128.26,120.57,113.93,104.08,93.63,85.00,79.17,69.91,65.13,60.73,54.10,55.29,63.17,58.96,64.51],
  high:   [23.73,31.49,44.55,69.94,71.85,80.28,93.39,107.42,121.94,136.37,149.99,161.97,171.49,177.81,180.42,179.07,176.93,168.37,159.60,146.63,132.37,119.55,109.43,95.98,87.30,79.35,69.59,68.07,73.63,67.48,71.41],
};
const MKT_2050 = {low:51.84,medium:64.51,high:71.41};
 
// ─── MARKET SEGMENTATION (BI, April 2026) ─────────────────────────────────────
const SEG = {
  gm:      [38.04,42.31,43.22,33.38,35.48,38.88,37.49,35.95,34.55,33.32,32.68,32.16,31.4,30.65,29.89,29.89,29.89,29.89,29.89,29.89,29.89,29.89,29.89,29.89,29.89,29.89,29.89,29.89,29.89,29.89,29.89],
  agri:    [0,0,0,2.27,2.27,3.4,4.54,5.88,7.22,7.56,7.9,8.25,8.93,9.62,10.31,10.31,10.31,10.31,10.31,10.31,10.31,10.31,10.31,10.31,10.31,10.31,10.31,10.31,10.31,10.31,10.31],
  floating:[0,0,0,2.71,2.53,2.99,3.96,3.91,3.86,4.56,4.77,4.95,5.02,5.09,5.15,5.15,5.15,5.15,5.15,5.15,5.15,5.15,5.15,5.15,5.15,5.15,5.15,5.15,5.15,5.15,5.15],
  res:     [25.44,23.69,23.32,26.25,23.42,21.21,21.39,21.2,20.87,20.9,20.9,20.66,20.66,20.66,20.66,20.66,20.66,20.66,20.66,20.66,20.66,20.66,20.66,20.66,20.66,20.66,20.66,20.66,20.66,20.66,20.66],
  ci:      [36.52,34.0,33.46,34.1,34.79,31.79,30.7,30.6,29.96,29.9,29.94,29.45,29.45,29.45,29.45,29.45,29.45,29.45,29.45,29.45,29.45,29.45,29.45,29.45,29.45,29.45,29.45,29.45,29.45,29.45,29.45],
  bipv:    [0,0,0,1.29,1.3,1.4,1.5,1.8,2.1,2.2,2.2,2.5,2.5,2.5,2.5,2.5,2.5,2.5,2.5,2.5,2.5,2.5,2.5,2.5,2.5,2.5,2.5,2.5,2.5,2.5,2.5],
  iipv:    [0,0,0,0,0.2,0.3,0.4,0.7,1.5,1.6,1.6,2.0,2.0,2.0,2.0,2.0,2.0,2.0,2.0,2.0,2.0,2.0,2.0,2.0,2.0,2.0,2.0,2.0,2.0,2.0,2.0],
};
const SEG_META = [
  {key:"gm",      label:"Ground-mounted", color:"#E8902A"},
  {key:"agri",    label:"Agrivoltaics",   color:"#8BA888"},
  {key:"floating",label:"Floating PV",    color:"#5BA8D0"},
  {key:"res",     label:"Residential",    color:"#7B91B5"},
  {key:"ci",      label:"C&I",            color:"#3F8FBF"},
  {key:"bipv",    label:"BIPV",           color:"#C25E8C"},
  {key:"iipv",    label:"IIPV",           color:"#152A4A"},
];
 
// ─── TECHNOLOGIES ─────────────────────────────────────────────────────────────
const TECHS = [
  {key:"perc",  label:"PERC",            cSi:true, color:"#B8C5D6"},
  {key:"topcon",label:"TOPCon",           cSi:true, color:"#5BA8D0"},
  {key:"hjt",   label:"HJT",              cSi:true, color:"#3F8FBF"},
  {key:"ibc",   label:"IBC / xBC",        cSi:true, color:"#E8902A"},
  {key:"tandem",label:"Tandem c-Si/Pks",  cSi:true, color:"#152A4A"},
  {key:"others",label:"Others (TF etc.)",  cSi:false,color:"#8898AB"},
];
const TK = TECHS.map(t=>t.key);
 
// Production waypoints: 2025 and 2035 are intermediate, 2050 is editable per pathway
const PROD_2025 = {perc:5,topcon:60,hjt:20,ibc:5,tandem:0,others:10};
const PROD_2035_DEFAULT = {
  1:{perc:0,topcon:65,hjt:15,ibc:10,tandem:0,others:10},
  2:{perc:0,topcon:50,hjt:20,ibc:18,tandem:5,others:7},
  3:{perc:0,topcon:35,hjt:18,ibc:25,tandem:12,others:10},
};
const PROD_2050_DEFAULT = {
  1:{perc:0,topcon:70,hjt:15,ibc:10,tandem:0, others:5},
  2:{perc:0,topcon:45,hjt:25,ibc:20,tandem:5, others:5},
  3:{perc:0,topcon:25,hjt:20,ibc:30,tandem:15,others:10},
};
 
// ─── PATHWAYS ─────────────────────────────────────────────────────────────────
const PW = {
  1:{label:"Pathway 1",name:"Stay in the Game",         color:"#7B91B5",pct:25, inf:2035,plat:2046},
  2:{label:"Pathway 2",name:"Build a Competitive Base",  color:"#3F8FBF",pct:50, inf:2031,plat:2042},
  3:{label:"Pathway 3",name:"Lead the Transition",       color:"#152A4A",pct:100,inf:2028,plat:2038},
};
const FILTERS = {
  1:{capexLo:0.3,capexHi:1.5, fteLo:10,fteHi:30, rampLo:0.5,rampHi:3},
  2:{capexLo:2.0,capexHi:6.0, fteLo:20,fteHi:80, rampLo:2,  rampHi:15},
  3:{capexLo:5.0,capexHi:12.0,fteLo:50,fteHi:150,rampLo:8,  rampHi:25},
};
// Each pathway lists ONLY its own new actions. The Gantt renders cumulatively
// (P2 view shows P1+P2, P3 view shows P1+P2+P3) via foldable groups in the UI.
const PW_ACTIONS = {
  1:[
    {tag:"D↓",title:"NZIA/IAA implementation",start:2026,end:2027,desc:"40% auction floor, resilience auctions, fast-track permitting",phase:"build"},
    {tag:"D↓",title:"NZIA/IAA sustained enforcement",start:2028,end:2050,desc:"Continued enforcement across all 27 MS",phase:"maintain"},
    {tag:"I↓",title:"Forced Labour Regulation enforcement",start:2026,end:2027,desc:"Customs enforcement on PV imports",phase:"build"},
    {tag:"I↓",title:"FLR sustained enforcement",start:2028,end:2050,desc:"Continued customs enforcement",phase:"maintain"},
  ],
  2:[
    {tag:"D↓",title:"Local content incentives",start:2027,end:2030,desc:"Bonus scoring in PV tenders for EU-manufactured modules",phase:"build"},
    {tag:"D↓",title:"Local content incentives (phase-out)",start:2031,end:2045,desc:"Gradual phase-out as EU industry matures",phase:"maintain"},
    {tag:"I↓",title:"Sustainability standards (CBAM extension)",start:2027,end:2030,desc:"Carbon border adjustment + expanded FLR scope on PV",phase:"build"},
    {tag:"I↓",title:"Sustainability standards (sustained)",start:2031,end:2050,desc:"Continued enforcement and review",phase:"maintain"},
    {tag:"D↑",title:"EIB fast-track financing window",start:2027,end:2030,desc:"Low/zero-interest lending, <6mo decisions for PV manufacturing",phase:"build"},
    {tag:"D↑",title:"EIB window (transitional)",start:2031,end:2035,desc:"Phase-out as private finance matures",phase:"maintain"},
    {tag:"I↑",title:"EU workforce & skills programme",start:2027,end:2030,desc:"~20k FTE target by 2030",phase:"build"},
    {tag:"I↑",title:"Workforce retraining & tech transitions",start:2031,end:2050,desc:"Continued retraining for new technologies",phase:"maintain"},
    {tag:"D↑",title:"Public co-investment in key projects",start:2026,end:2030,desc:"Equity/grant participation in anchor manufacturing projects",phase:"build"},
    {tag:"D↑",title:"Public co-investment (exit phase)",start:2031,end:2035,desc:"Gradual divestment as projects self-sustain",phase:"maintain"},
  ],
  3:[
    {tag:"D↓",title:"Local content requirements",start:2028,end:2032,desc:"Mandatory EU-manufactured share in utility-scale tenders",phase:"build"},
    {tag:"D↓",title:"Local content requirements (sustained)",start:2033,end:2045,desc:"Gradual relaxation as domestic capacity matures",phase:"maintain"},
    {tag:"D↑",title:"Production-linked grants",start:2028,end:2035,desc:"Per-GW manufacturing subsidies (IRA 45X-style)",phase:"build"},
    {tag:"D↑",title:"Production-linked grants (phase-down)",start:2036,end:2045,desc:"Reduction as cost gap closes",phase:"maintain"},
    {tag:"I↓",title:"Trade defence (tariffs, safeguards)",start:2028,end:2030,desc:"Anti-dumping duties, safeguard tariffs on imports",phase:"build"},
    {tag:"I↓",title:"Trade defence (review & phase-out)",start:2031,end:2045,desc:"5-yr review cycles, gradual removal",phase:"maintain"},
    {tag:"D↑",title:"Concessional debt & public guarantees",start:2028,end:2035,desc:"Sovereign-backed debt, risk-sharing facilities",phase:"build"},
    {tag:"D↑",title:"Concessional debt (phase-down)",start:2036,end:2045,desc:"Gradual reduction as private debt market matures",phase:"maintain"},
    {tag:"D↑",title:"Public equity & financing vehicle",start:2028,end:2035,desc:"Sovereign equity stakes, single-window financing institution",phase:"build"},
    {tag:"D↑",title:"Public equity (divestment phase)",start:2036,end:2050,desc:"Long-term divestment as projects self-sustain",phase:"maintain"},
  ],
};
 
const C0_MOD_OP=10.5, C0_CELL_OP=2.0;
 
// ─── THEME ────────────────────────────────────────────────────────────────────
const C={bg:"#F4F7FB",panel:"#FFFFFF",card:"#FFFFFF",border:"#D6E0EC",
  accent:"#3F8FBF",text:"#1A2A44",muted:"#6B7E96",label:"#4A6178",
  brandNavy:"#152A4A",brandOrange:"#E8902A",brandLight:"#A8C5DC"};
const inp={background:"#F8FAFC",border:`1px solid ${C.border}`,borderRadius:4,padding:"3px 6px",color:C.text,outline:"none"};
const SC={mod:"#E8902A",cell:"#3F8FBF",wafer:"#152A4A"};
 
// ─── MATH ─────────────────────────────────────────────────────────────────────
const lerp=(a,b,t)=>a+(b-a)*Math.max(0,Math.min(1,t));
function piecewiseMix(waypoints,year){
  const yrs=Object.keys(waypoints).map(Number).sort((a,b)=>a-b);
  if(year<=yrs[0]) return {...waypoints[yrs[0]]};
  if(year>=yrs[yrs.length-1]) return {...waypoints[yrs[yrs.length-1]]};
  let i=0; while(i<yrs.length-1&&year>yrs[i+1]) i++;
  const y0=yrs[i],y1=yrs[i+1],t=(year-y0)/(y1-y0);
  const w0=waypoints[y0],w1=waypoints[y1];
  return Object.fromEntries(TK.map(k=>[k,lerp(w0[k]||0,w1[k]||0,t)]));
}
function cSiShare(pm){return TECHS.filter(t=>t.cSi).reduce((s,t)=>s+(pm[t.key]||0),0)/100;}
function scurve(ceilNP,c0NP,infY,platY,lag=0){
  const safePlat=Math.max(platY,infY+lag+1);
  const k=Math.log(19)/(safePlat-infY);
  return YEARS.map(y=>{
    if(y<=2025) return {year:y,cap:c0NP};
    const v=c0NP+(ceilNP-c0NP)/(1+Math.exp(-k*(y-(infY+lag))));
    return {year:y,cap:+Math.max(c0NP,v).toFixed(2)};
  });
}
function lineBuilds(curve,lifetime){
  const b=new Array(YEARS.length).fill(0);
  for(let i=1;i<YEARS.length;i++){
    if(YEARS[i]<FORECAST_START) continue;
    const netNew=Math.max(0,curve[i].cap-curve[i-1].cap);
    const ri=YEARS.indexOf(YEARS[i]-lifetime);
    b[i]=+(netNew+(ri>=0?b[ri]:0)).toFixed(3);
  }
  return b;
}
function smoothCapex(bld,wCapexPerGW,ltConstr){
  const spend=new Array(YEARS.length).fill(0);
  const replWindow=3;
  for(let i=0;i<YEARS.length;i++){
    if(bld[i]===0) continue;
    const totalCost=bld[i]*wCapexPerGW/1000;
    const window=Math.max(ltConstr+1,replWindow);
    const share=totalCost/window;
    for(let d=0;d<window;d++){
      const ti=i-d+1;
      if(ti>=0&&ti<YEARS.length) spend[ti]=+(spend[ti]+share).toFixed(4);
    }
  }
  return spend;
}
function applyRampDelay(curve,rampYrs){
  if(rampYrs<=0) return curve;
  const eff=curve.map(d=>d.cap);
  for(let i=1;i<YEARS.length;i++){
    if(YEARS[i]<FORECAST_START) continue;
    const added=Math.max(0,curve[i].cap-curve[i-1].cap);
    if(added<=0) continue;
    for(let d=0;d<rampYrs;d++){
      const idx=i+d;
      if(idx>=YEARS.length) break;
      const penalty=added*((rampYrs-d)/(rampYrs+1));
      eff[idx]-=penalty;
    }
  }
  return eff.map((cap,i)=>({year:YEARS[i],cap:+Math.max(curve[0].cap,cap).toFixed(2)}));
}
 
// ─── UI COMPONENTS ────────────────────────────────────────────────────────────
const SecLbl=({c})=>(<div style={{fontSize:9,fontWeight:700,color:C.muted,letterSpacing:"1.2px",textTransform:"uppercase",marginBottom:4}}>{c}</div>);
const Card=({children,style})=>(<div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 14px",...style}}>{children}</div>);
const Chip=({active,color,onClick,children})=>(<button onClick={onClick} style={{padding:"4px 10px",borderRadius:4,border:`1px solid ${active?color:C.border}`,background:active?`${color}22`:"transparent",color:active?color:C.label,fontSize:11,fontWeight:600,cursor:"pointer"}}>{children}</button>);
const Toggle=({val,onChange,opts=["%","GW"]})=>(<div style={{display:"flex",gap:3}}>{opts.map(v=>(<button key={v} onClick={()=>onChange(v)} style={{padding:"1px 7px",borderRadius:3,border:`1px solid ${val===v?C.accent:C.border}`,background:val===v?`${C.accent}22`:"transparent",color:val===v?C.accent:C.muted,fontSize:9,cursor:"pointer"}}>{v}</button>))}</div>);
function Slider({label,val,min,max,step,fmt,onChange}){return(<div style={{display:"flex",flexDirection:"column",gap:2}}><div style={{display:"flex",justifyContent:"space-between"}}><span style={{fontSize:11,color:C.label}}>{label}</span><span style={{fontSize:11,fontWeight:700,color:C.text}}>{fmt?fmt(val):val}</span></div><input type="range" min={min} max={max} step={step} value={val} onChange={e=>onChange(+e.target.value)} style={{width:"100%",accentColor:C.accent,cursor:"pointer"}}/></div>);}
function Num({label,val,unit,min,max,step,onChange,w=54}){return(<div style={{display:"flex",flexDirection:"column",gap:2}}>{label&&<span style={{fontSize:9,color:C.label}}>{label}</span>}<div style={{display:"flex",gap:4,alignItems:"center"}}><input type="number" value={val} min={min} max={max} step={step} onChange={e=>onChange(+e.target.value)} style={{...inp,width:w,fontSize:10}}/><span style={{fontSize:9,color:C.muted,whiteSpace:"nowrap"}}>{unit}</span></div></div>);}
function Foldable({title,children,defaultOpen=false}){const[open,setOpen]=useState(defaultOpen);return(<div><div onClick={()=>setOpen(o=>!o)} style={{cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:`1px solid ${C.border}`,marginBottom:open?8:0}}><span style={{fontSize:9,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"1px"}}>{title}</span><span style={{fontSize:10,color:C.muted}}>{open?"▲":"▼"}</span></div>{open&&<div style={{paddingTop:4}}>{children}</div>}</div>);}
function PwGanttGroup({group,children}){
  const [open,setOpen]=useState(group.isCurrent);
  return(
    <div style={{borderTop:`1px solid ${C.border}`,marginTop:4}}>
      <div onClick={()=>setOpen(o=>!o)}
        style={{cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",
          padding:"6px 8px",background:`${group.color}14`,borderLeft:`3px solid ${group.color}`}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:8,fontWeight:700,color:group.color,letterSpacing:"1px",textTransform:"uppercase"}}>{group.label}</span>
          <span style={{fontSize:10,fontWeight:600,color:C.text}}>{group.name}</span>
          <span style={{fontSize:8,color:C.muted}}>· {group.acts.length} action{group.acts.length===1?"":"s"}</span>
        </div>
        <span style={{fontSize:10,color:group.color,fontWeight:700}}>{open?"▲":"▼"}</span>
      </div>
      {open&&<div style={{paddingTop:2}}>{children}</div>}
    </div>
  );
}
function TT({active,payload,label}){if(!active||!payload?.length) return null;return(<div style={{background:"#FFFFFF",border:`1px solid ${C.border}`,padding:"8px 12px",borderRadius:6,fontSize:10,maxWidth:230,boxShadow:"0 2px 8px rgba(21,42,74,0.12)"}}><div style={{fontWeight:700,color:C.text,marginBottom:4}}>{label}</div>{payload.filter(p=>Math.abs(+p.value||0)>0.005).map(p=>(<div key={p.name} style={{color:p.fill||p.stroke||C.label,margin:"2px 0"}}>{p.name}: <strong style={{color:C.text}}>{(+p.value).toFixed(2)}</strong></div>))}</div>);}
 
// ─── APP ──────────────────────────────────────────────────────────────────────
export default function App(){
  useEffect(()=>{
    const prevBg=document.body.style.background;
    const prevFont=document.body.style.fontFamily;
    document.body.style.background="#E8EEF5";
    document.body.style.fontFamily="'DM Sans','Segoe UI',sans-serif";
    return()=>{document.body.style.background=prevBg;document.body.style.fontFamily=prevFont;};
  },[]);
  const [scen,setScen]=useState("medium");
  const [pw,setPw]=useState(2);
  const [pct,setPct]=useState(PW[2].pct);
  const [infY,setInfY]=useState(PW[2].inf);
  const [platY,setPlatY]=useState(PW[2].plat);
  const [cellLag,setCellLag]=useState(3);
  const [waferLag,setWaferLag]=useState(3);
  const [ltConstrMod,setLtConstrMod]=useState(1);
  const [ltConstrCell,setLtConstrCell]=useState(2);
  const [ltConstrWafer,setLtConstrWafer]=useState(2);
  const [modNP,setModNP]=useState(1.30);
  const [cellNP,setCellNP]=useState(1.20);
  const [waferNP,setWaferNP]=useState(1.15);
  const [ltMod,setLtMod]=useState(5);
  const [ltCell,setLtCell]=useState(5);
  const [ltWafer,setLtWafer]=useState(5);
  const [fteMod,setFteMod]=useState(750);
  const [fteCell,setFteCell]=useState(550);
  const [fteWafer,setFteWafer]=useState(290);
 
  const [rampDelay,setRampDelay]=useState(1);
 
  const [prod2035t,setProd2035t]=useState({...PROD_2035_DEFAULT[2]});
  const [prod2050t,setProd2050t]=useState({...PROD_2050_DEFAULT[2]});
 
  const [capex,setCapex]=useState({
    topcon:{wafer:130,cell:170,mod:60},hjt:{wafer:125,cell:260,mod:55},
    ibc:{wafer:130,cell:175,mod:60},tandem:{wafer:105,cell:265,mod:50},
    others:{wafer:0,cell:0,mod:190},
  });
 
  const [prodView,setProdView]=useState("%");
  const [originView,setOriginView]=useState("Annual");
  const [segView,setSegView]=useState("GW");
 
  const selPw=p=>{setPw(p);setPct(PW[p].pct);setInfY(PW[p].inf);setPlatY(PW[p].plat);
    setProd2035t({...PROD_2035_DEFAULT[p]});setProd2050t({...PROD_2050_DEFAULT[p]});};
 
  // ── Curves ──────────────────────────────────────────────────────────────────
  const m2050=MKT_2050[scen];
  const c0ModNP=+(C0_MOD_OP*modNP).toFixed(2);
  const c0CellNP=+(C0_CELL_OP*cellNP).toFixed(2);
  const cSi50=cSiShare(prod2050t);
  const ceilModNP=+(pct/100*m2050*modNP).toFixed(2);
  const ceilCellNP=+(ceilModNP*cSi50).toFixed(2);
 
  const modCurveRaw=useMemo(()=>scurve(ceilModNP,c0ModNP,infY,platY,0),[ceilModNP,c0ModNP,infY,platY]);
  const cellCurveRaw=useMemo(()=>scurve(ceilCellNP,c0CellNP,infY,platY,cellLag),[ceilCellNP,c0CellNP,infY,platY,cellLag]);
  const waferCurveRaw=useMemo(()=>scurve(ceilCellNP,0,infY,platY,cellLag+waferLag),[ceilCellNP,infY,platY,cellLag,waferLag]);
 
  const modCurve=useMemo(()=>applyRampDelay(modCurveRaw,rampDelay),[modCurveRaw,rampDelay]);
  const cellCurve=useMemo(()=>applyRampDelay(cellCurveRaw,rampDelay),[cellCurveRaw,rampDelay]);
  const waferCurve=useMemo(()=>applyRampDelay(waferCurveRaw,rampDelay),[waferCurveRaw,rampDelay]);
 
  const bldMod=useMemo(()=>lineBuilds(modCurveRaw,ltMod),[modCurveRaw,ltMod]);
  const bldCell=useMemo(()=>lineBuilds(cellCurveRaw,ltCell),[cellCurveRaw,ltCell]);
  const bldWafer=useMemo(()=>lineBuilds(waferCurveRaw,ltWafer),[waferCurveRaw,ltWafer]);
 
  const wCapexMod=TK.reduce((s,k)=>{const c=capex[k];return c?s+(prod2050t[k]||0)/100*(c.mod||0):s;},0);
  const wCapexCell=TK.reduce((s,k)=>{const c=capex[k];return c?s+(prod2050t[k]||0)/100*(c.cell||0):s;},0);
  const wCapexWafer=TK.reduce((s,k)=>{const c=capex[k];return c?s+(prod2050t[k]||0)/100*(c.wafer||0):s;},0);
 
  const spendMod=useMemo(()=>smoothCapex(bldMod,wCapexMod,ltConstrMod),[bldMod,wCapexMod,ltConstrMod]);
  const spendCell=useMemo(()=>smoothCapex(bldCell,wCapexCell,ltConstrCell),[bldCell,wCapexCell,ltConstrCell]);
  const spendWafer=useMemo(()=>smoothCapex(bldWafer,wCapexWafer,ltConstrWafer),[bldWafer,wCapexWafer,ltConstrWafer]);
 
  const YRS25=YEARS.filter(y=>y>=CHART_START);
  const flt=FILTERS[pw];
 
  const prodWP=useMemo(()=>({2025:PROD_2025,2035:prod2035t,2050:prod2050t}),[prod2035t,prod2050t]);
 
  // ── Chart data ──────────────────────────────────────────────────────────────
  const gCap=useMemo(()=>YRS25.map(y=>{const i=YEARS.indexOf(y);return{year:y,opMod:+(modCurve[i].cap/modNP).toFixed(2),bldMod:+bldMod[i].toFixed(2),bldCell:+bldCell[i].toFixed(2),bldWafer:+bldWafer[i].toFixed(2),rampLo:flt.rampLo,rampHi:flt.rampHi};}),[modCurve,modNP,bldMod,bldCell,bldWafer,flt.rampLo,flt.rampHi]);
 
  const gOriginAnn=useMemo(()=>YRS25.map(y=>{const i=YEARS.indexOf(y);const demand=MKT_ANN[scen][i];if(y<FORECAST_START) return{year:y,imported:+demand.toFixed(2),modOnly:0,modCell:0,fullChain:0};const opMod=modCurve[i].cap/modNP,opCell=cellCurve[i].cap/cellNP,opWafer=waferCurve[i].cap/waferNP;const euMod=Math.min(opMod,demand),euCell=Math.min(opCell,euMod),euWafer=Math.min(opWafer,euCell);return{year:y,imported:+Math.max(0,demand-euMod).toFixed(2),modOnly:+Math.max(0,euMod-euCell).toFixed(2),modCell:+Math.max(0,euCell-euWafer).toFixed(2),fullChain:+Math.max(0,euWafer).toFixed(2)};}),[scen,modCurve,cellCurve,waferCurve,modNP,cellNP,waferNP]);
 
  const gOriginCum=useMemo(()=>{let acc={imported:0,modOnly:0,modCell:0,fullChain:0};return gOriginAnn.map(r=>{acc={imported:+(acc.imported+r.imported).toFixed(2),modOnly:+(acc.modOnly+r.modOnly).toFixed(2),modCell:+(acc.modCell+r.modCell).toFixed(2),fullChain:+(acc.fullChain+r.fullChain).toFixed(2)};return{year:r.year,...acc};});},[gOriginAnn]);
  const gOrigin=originView==="Annual"?gOriginAnn:gOriginCum;
 
  const gProdTech=useMemo(()=>YRS25.map(y=>{const i=YEARS.indexOf(y);const mix=piecewiseMix(prodWP,y);const opGW=modCurve[i].cap/modNP;const row={year:y};TECHS.forEach(t=>{row[t.key]=prodView==="%"?+(mix[t.key]||0).toFixed(1):+(opGW*(mix[t.key]||0)/100).toFixed(2);});return row;}),[modCurve,modNP,prodWP,prodView]);
 
  const gSeg=useMemo(()=>YRS25.map(y=>{const i=YEARS.indexOf(y);const total=MKT_ANN[scen][i];const row={year:y};
    if(segView==="%"){
      const rawSum=SEG_META.reduce((s,seg)=>s+(SEG[seg.key][i]||0),0);
      SEG_META.forEach(s=>{row[s.key]=+((SEG[s.key][i]||0)/rawSum*100).toFixed(1);});
    } else {
      SEG_META.forEach(s=>{row[s.key]=+(total*(SEG[s.key][i]||0)/100).toFixed(2);});
    }
    return row;}),[scen,segView]);
 
  const gCapexStack=useMemo(()=>YRS25.map(y=>{const i=YEARS.indexOf(y);return{year:y,modCapex:+spendMod[i].toFixed(3),cellCapex:+spendCell[i].toFixed(3),waferCapex:+spendWafer[i].toFixed(3),capexLo:flt.capexLo,capexHi:flt.capexHi};}),[spendMod,spendCell,spendWafer,flt.capexLo,flt.capexHi]);
 
  const gFteStack=useMemo(()=>YRS25.map(y=>{const i=YEARS.indexOf(y);return{year:y,modFte:+(modCurve[i].cap/modNP*fteMod/1000).toFixed(2),cellFte:+(cellCurve[i].cap/cellNP*fteCell/1000).toFixed(2),waferFte:+(waferCurve[i].cap/waferNP*fteWafer/1000).toFixed(2),fteLo:flt.fteLo,fteHi:flt.fteHi};}),[modCurve,cellCurve,waferCurve,modNP,cellNP,waferNP,fteMod,fteCell,fteWafer,flt.fteLo,flt.fteHi]);
 
  const gScen=useMemo(()=>YRS25.map(y=>{const i=YEARS.indexOf(y);return{year:y,low:MKT_ANN.low[i],medium:MKT_ANN.medium[i],high:MKT_ANN.high[i]};}),[]);
 
  // KPIs
  const capAt=yr=>modCurve.find(d=>d.year===yr)?.cap??0;
  const cap30=capAt(2030),cap40=capAt(2040),cap50=capAt(2050);
  const share50=+(cap50/modNP/m2050*100).toFixed(0);
  const totalCapex=+(gCapexStack.reduce((s,d)=>s+d.modCapex+d.cellCapex+d.waferCapex,0)).toFixed(1);
  const peakCapexRow=gCapexStack.reduce((mx,d)=>{const tot=d.modCapex+d.cellCapex+d.waferCapex;return tot>mx.val?{val:tot,year:d.year}:mx;},{val:0,year:0});
  const fte2050=(gFteStack[gFteStack.length-1]?.modFte??0)+(gFteStack[gFteStack.length-1]?.cellFte??0)+(gFteStack[gFteStack.length-1]?.waferFte??0);
  const peakFteRow=gFteStack.reduce((mx,d)=>{const tot=d.modFte+d.cellFte+d.waferFte;return tot>mx.val?{val:tot,year:d.year}:mx;},{val:0,year:0});
  const pwColor=PW[pw].color;
  const prodSum35=TK.reduce((s,k)=>s+(prod2035t[k]||0),0);
  const prodSum50=TK.reduce((s,k)=>s+(prod2050t[k]||0),0);
 
  const xt={fontSize:9,fill:C.muted};const yt={fontSize:9,fill:C.muted};
  const CH=195,YW=52,CHB=210;
 
  return(
    <div style={{fontFamily:"'DM Sans','Segoe UI',sans-serif",background:C.bg,minHeight:"100vh",color:C.text,display:"flex",flexDirection:"column",maxWidth:1800,margin:"0 auto",width:"100%",boxShadow:"0 0 24px rgba(21,42,74,0.06)"}}>
 
      {/* HEADER */}
      <div style={{background:C.panel,borderBottom:`1px solid ${C.border}`,padding:"18px 24px",display:"flex",alignItems:"center",gap:16}}>
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          <a href="/" style={{fontSize:11,fontWeight:600,color:C.accent,textDecoration:"none",letterSpacing:"0.3px"}}>← Back to Solar Manufacturing Tool</a>
          <div style={{fontSize:24,fontWeight:700,color:C.brandNavy,letterSpacing:"-0.3px"}}>EU PV Industrial Trajectory Model</div>
        </div>
        <div style={{marginLeft:"auto",display:"flex",gap:16,alignItems:"center"}}>
          <a href="https://www.becquerelinstitute.eu" target="_blank" rel="noopener noreferrer">
            <img src="/BI.png" alt="Becquerel Institute" style={{height:80,objectFit:"contain",cursor:"pointer"}}/>
          </a>
          <a href="https://ibc4.eu" target="_blank" rel="noopener noreferrer">
            <img src="/ibc4u-logo.png" alt="IBC4EU" style={{height:40,objectFit:"contain",cursor:"pointer"}}/>
          </a>
        </div>
      </div>
 
      <div style={{display:"grid",gridTemplateColumns:"400px 1fr",flex:1,minHeight:0,overflow:"hidden"}}>
 
        {/* ── SIDEBAR ──────────────────────────────────────────────────── */}
        <div style={{background:C.panel,borderRight:`1px solid ${C.border}`,padding:"12px 11px",overflowY:"auto",display:"flex",flexDirection:"column",gap:14}}>
 
          <div>
            <SecLbl c="Market Scenario"/>
            <div style={{display:"flex",gap:5}}>
              {[["low","#E8902A"],["medium","#3F8FBF"],["high","#152A4A"]].map(([s,col])=>(<Chip key={s} active={scen===s} color={col} onClick={()=>setScen(s)}>{s[0].toUpperCase()+s.slice(1)}</Chip>))}
            </div>
            <div style={{fontSize:9,color:C.muted,marginTop:3}}>2050 demand: {m2050.toFixed(1)} GW/yr</div>
            <Foldable title="Scenario comparison" defaultOpen={false}>
              <ResponsiveContainer width="100%" height={100}>
                <ComposedChart data={gScen} margin={{top:4,right:4,left:0,bottom:0}}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                  <XAxis dataKey="year" stroke={C.muted} tick={xt} tickLine={false} ticks={[2025,2030,2035,2040,2045,2050]} tickFormatter={v=>`'${String(v).slice(2)}`}/>
                  <YAxis stroke={C.muted} tick={yt} tickLine={false} width={YW} unit=" GW"/>
                  <Tooltip content={<TT/>}/>
                  {[["high","#152A4A"],["medium","#3F8FBF"],["low","#E8902A"]].map(([k,col])=>(<Line key={k} dataKey={k} stroke={col} strokeWidth={scen===k?2.5:1} strokeOpacity={scen===k?1:0.3} strokeDasharray={scen===k?"none":"4 2"} dot={false}/>))}
                </ComposedChart>
              </ResponsiveContainer>
            </Foldable>
          </div>
 
          <div>
            <SecLbl c="Industrial Pathway"/>
            {[1,2,3].map(p=>(<div key={p} onClick={()=>selPw(p)} style={{padding:"6px 9px",borderRadius:6,cursor:"pointer",marginBottom:4,border:`1px solid ${pw===p?PW[p].color:C.border}`,background:pw===p?`${PW[p].color}1F`:C.card}}><span style={{fontSize:9,fontWeight:700,color:pw===p?PW[p].color:C.muted,textTransform:"uppercase"}}>{PW[p].label}  </span><span style={{fontSize:11,fontWeight:600,color:C.text}}>{PW[p].name}</span><div style={{fontSize:9,color:C.muted,marginTop:1}}>Target {PW[p].pct}% · Inflection {PW[p].inf} · Plateau {PW[p].plat}</div></div>))}
          </div>
 
          <Foldable title="Trajectory Parameters" defaultOpen={true}>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <Slider label="EU module target (% of 2050 market)" val={pct} min={5} max={110} step={1} fmt={v=>`${v}% → ${(v/100*m2050).toFixed(1)} GW op.`} onChange={setPct}/>
              <Slider label="Inflection year" val={infY} min={2026} max={2044} step={1} onChange={v=>setInfY(Math.min(v,platY-1))}/>
              <Slider label="Plateau year (95%)" val={platY} min={2027} max={2055} step={1} onChange={v=>setPlatY(Math.max(v,infY+1))}/>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                <Slider label="Cell lag" val={cellLag} min={0} max={10} step={1} fmt={v=>`${v} yr`} onChange={setCellLag}/>
                <Slider label="Wafer lag" val={waferLag} min={0} max={10} step={1} fmt={v=>`${v} yr`} onChange={setWaferLag}/>
              </div>
            </div>
          </Foldable>
 
          <Foldable title="Production Tech Mix" defaultOpen={false}>
            <div style={{fontSize:9,color:C.muted,marginBottom:5}}>Piecewise: 2025 (fixed) → 2035 → 2050</div>
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:6,overflow:"hidden",fontSize:10}}>
              <div style={{display:"grid",gridTemplateColumns:"90px 1fr 1fr 1fr",padding:"4px 7px",borderBottom:`1px solid ${C.border}`,fontSize:8,fontWeight:700,color:C.muted,gap:3}}>
                <span>Technology</span><span style={{textAlign:"center"}}>'25</span><span style={{textAlign:"center"}}>'35 ▾</span><span style={{textAlign:"center"}}>'50 ▾</span>
              </div>
              {TECHS.map((tech,i)=>(
                <div key={tech.key} style={{display:"grid",gridTemplateColumns:"90px 1fr 1fr 1fr",padding:"3px 7px",gap:3,alignItems:"center",borderBottom:i<TECHS.length-1?`1px solid ${C.border}`:"none",background:i%2===0?"transparent":"#F8FAFC"}}>
                  <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:6,height:6,borderRadius:"50%",background:tech.color,flexShrink:0}}/><span style={{fontSize:9,color:C.text}}>{tech.label}</span></div>
                  <div style={{textAlign:"center",color:C.muted}}>{PROD_2025[tech.key]||0}%</div>
                  <input type="number" value={prod2035t[tech.key]||0} min={0} max={100} step={1} onChange={e=>setProd2035t(m=>({...m,[tech.key]:+e.target.value}))} style={{...inp,width:"100%",fontSize:10,padding:"1px 3px",textAlign:"center"}}/>
                  <input type="number" value={prod2050t[tech.key]||0} min={0} max={100} step={1} onChange={e=>setProd2050t(m=>({...m,[tech.key]:+e.target.value}))} style={{...inp,width:"100%",fontSize:10,padding:"1px 3px",textAlign:"center"}}/>
                </div>
              ))}
              <div style={{display:"grid",gridTemplateColumns:"90px 1fr 1fr 1fr",padding:"3px 7px",borderTop:`1px solid ${C.border}`,gap:3,fontSize:9}}>
                <span style={{color:C.muted}}>Total</span>
                <span style={{textAlign:"center",color:C.muted}}>100%</span>
                <span style={{textAlign:"center",fontWeight:700,color:Math.abs(prodSum35-100)<1?"#3F8FBF":"#C25E5E"}}>{prodSum35}%</span>
                <span style={{textAlign:"center",fontWeight:700,color:Math.abs(prodSum50-100)<1?"#3F8FBF":"#C25E5E"}}>{prodSum50}%</span>
              </div>
            </div>
          </Foldable>
 
          <Foldable title="Technical Parameters" defaultOpen={false}>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <div style={{display:"grid",gridTemplateColumns:"90px 1fr 1fr 1fr",gap:6,fontSize:8,fontWeight:700,color:C.muted,textTransform:"uppercase",paddingBottom:4,borderBottom:`1px solid ${C.border}`}}>
                <span/><span style={{textAlign:"center"}}>Module</span><span style={{textAlign:"center"}}>Cell</span><span style={{textAlign:"center"}}>Wafer</span>
              </div>
              {[["Line lifetime",ltMod,setLtMod,ltCell,setLtCell,ltWafer,setLtWafer,"yr",5,25,1],
                ["Constr. lead",ltConstrMod,setLtConstrMod,ltConstrCell,setLtConstrCell,ltConstrWafer,setLtConstrWafer,"yr",0,5,1],
                ["NP inflation",modNP,setModNP,cellNP,setCellNP,waferNP,setWaferNP,"×",1.0,2.0,0.05],
                ["Workforce",fteMod,setFteMod,fteCell,setFteCell,fteWafer,setFteWafer,"FTE/GW",1,3000,1],
              ].map(([lbl,v1,s1,v2,s2,v3,s3,u,mn,mx,st])=>(
                <div key={lbl} style={{display:"grid",gridTemplateColumns:"90px 1fr 1fr 1fr",gap:6,alignItems:"center"}}>
                  <span style={{fontSize:9,color:C.label}}>{lbl}</span>
                  <Num val={v1} unit={u} min={mn} max={mx} step={st} onChange={s1}/>
                  <Num val={v2} unit={u} min={mn} max={mx} step={st} onChange={s2}/>
                  <Num val={v3} unit={u} min={mn} max={mx} step={st} onChange={s3}/>
                </div>
              ))}
              <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${C.border}`}}>
                <Slider label="Ramp-up delay (yrs to full output)" val={rampDelay} min={0} max={3} step={1}
                  fmt={v=>v===0?"None":`${v} yr → ${Math.round(100*v/(v+1))}% penalty yr1`} onChange={setRampDelay}/>
                <div style={{fontSize:8,color:C.muted,marginTop:2}}>IEA: new plants operate below rated capacity for 1–2 years [IEA-CTM]</div>
              </div>
            </div>
          </Foldable>
 
          <Foldable title="CAPEX per Technology (M€/GW)" defaultOpen={false}>
            <div style={{fontSize:8,color:C.muted,marginBottom:6,fontStyle:"italic"}}>VDMA Fig.11 × 1.5 EU × 2.5 total project. Real terms.</div>
            {TECHS.filter(t=>t.key!=="perc").map(tech=>(<div key={tech.key} style={{marginBottom:8}}><div style={{fontSize:9,fontWeight:700,color:tech.color,marginBottom:4,display:"flex",alignItems:"center",gap:4}}><div style={{width:5,height:5,borderRadius:"50%",background:tech.color}}/>{tech.label}</div><div style={{display:"grid",gridTemplateColumns:"90px 1fr 1fr 1fr",gap:6,alignItems:"center"}}><span/>{[["wafer","Wafer"],["cell","Cell"],["mod","Module"]].map(([f,lbl])=>(<Num key={f} label={lbl} val={capex[tech.key]?.[f]??0} unit="M€" min={0} max={1000} step={5} onChange={v=>setCapex(p=>({...p,[tech.key]:{...p[tech.key],[f]:v}}))}/>))}</div></div>))}
          </Foldable>
        </div>
 
        {/* ── MAIN ─────────────────────────────────────────────────────── */}
        <div style={{padding:"12px 14px",overflowY:"auto",display:"flex",flexDirection:"column",gap:11}}>
 
          {/* KPIs */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
            {[{v:cap30.toFixed(1),u:"GW NP",l:"Module cap. 2030",c:pwColor},{v:cap40.toFixed(1),u:"GW NP",l:"Module cap. 2040",c:pwColor},{v:cap50.toFixed(1),u:"GW NP",l:"Module cap. 2050",c:pwColor},{v:`${share50}%`,u:"of EU demand",l:"Market share 2050",c:C.accent}].map(k=>(
              <Card key={k.l} style={{borderTop:`2px solid ${k.c}`,padding:"8px 12px"}}><div style={{fontSize:20,fontWeight:800,textAlign:"center",color:C.text,lineHeight:1.1}}>{k.v}</div><div style={{fontSize:9,color:C.muted,textAlign:"center",marginTop:1}}>{k.u}</div><div style={{fontSize:9,color:C.label,textAlign:"center",marginTop:2}}>{k.l}</div></Card>
            ))}
          </div>
 
          {/* 2×2 charts */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:11}}>
 
            <Card>
              <div style={{fontSize:11,fontWeight:700}}>EU Industrial Capacity & Annual Line Builds</div>
              <div style={{fontSize:9,color:C.muted,marginBottom:8}}>GW — operational (line) · bars = lines commissioned</div>
              <ResponsiveContainer width="100%" height={CH}>
                <ComposedChart data={gCap} margin={{top:2,right:8,left:0,bottom:0}}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                  <XAxis dataKey="year" stroke={C.muted} tick={xt} tickLine={false}/>
                  <YAxis stroke={C.muted} tick={yt} tickLine={false} width={YW} unit=" GW" domain={[0,d=>Math.max(d,flt.rampHi*1.2)]}/>
                  <Tooltip content={<TT/>}/><Legend wrapperStyle={{fontSize:9,paddingTop:4}}/>
                  <ReferenceArea y1={flt.rampLo} y2={flt.rampHi} fill="#DC2626" fillOpacity={0.10} stroke="none" label={{value:`P${pw} filter: ${flt.rampLo}–${flt.rampHi} GW/yr`,fill:"#B91C1C",fontSize:8,position:"insideTopRight"}}/>
                  <Bar dataKey="bldMod" name="Mod." stackId="bld" fill={SC.mod} fillOpacity={0.85}/>
                  <Bar dataKey="bldCell" name="Cell" stackId="bld" fill={SC.cell} fillOpacity={0.85}/>
                  <Bar dataKey="bldWafer" name="Wafer" stackId="bld" fill={SC.wafer} fillOpacity={0.85}/>
                  <Line dataKey="opMod" name="EU oper. cap." stroke={pwColor} strokeWidth={2.5} dot={false}/>
                </ComposedChart>
              </ResponsiveContainer>
            </Card>
 
            <Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:2}}>
                <div><div style={{fontSize:11,fontWeight:700}}>EU PV Installations by Manufacturing Origin</div><div style={{fontSize:9,color:C.muted}}>{originView==="Annual"?"GW/yr":"GW cumulative"}</div></div>
                <Toggle val={originView} onChange={setOriginView} opts={["Annual","Cumul."]}/>
              </div>
              <ResponsiveContainer width="100%" height={CH}>
                <ComposedChart data={gOrigin} margin={{top:4,right:8,left:0,bottom:0}}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                  <XAxis dataKey="year" stroke={C.muted} tick={xt} tickLine={false}/>
                  <YAxis stroke={C.muted} tick={yt} tickLine={false} width={YW} unit=" GW"/>
                  <Tooltip content={<TT/>}/><Legend wrapperStyle={{fontSize:9,paddingTop:4}}/>
                  <Area dataKey="imported" stackId="s" name="Imported" fill="#B8C5D6" stroke="none" fillOpacity={0.9}/>
                  <Area dataKey="modOnly" stackId="s" name="EU modules only" fill="#A8C5DC" stroke="none" fillOpacity={0.9}/>
                  <Area dataKey="modCell" stackId="s" name="EU mod.+cells" fill="#5BA8D0" stroke="none" fillOpacity={0.9}/>
                  <Area dataKey="fullChain" stackId="s" name="EU full chain" fill="#152A4A" stroke="none" fillOpacity={0.9}/>
                </ComposedChart>
              </ResponsiveContainer>
            </Card>
 
            <Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:2}}>
                <div><div style={{fontSize:11,fontWeight:700}}>EU Production Capacity Tech Mix</div><div style={{fontSize:9,color:C.muted}}>Piecewise 2025→2035→2050</div></div>
                <Toggle val={prodView} onChange={setProdView}/>
              </div>
              <ResponsiveContainer width="100%" height={CH}>
                <BarChart data={gProdTech} margin={{top:4,right:8,left:0,bottom:0}}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                  <XAxis dataKey="year" stroke={C.muted} tick={xt} tickLine={false} tickFormatter={v=>v%5===0?v:""}/>
                  <YAxis stroke={C.muted} tick={yt} tickLine={false} width={YW} unit={prodView==="%"?"%":" GW"} domain={prodView==="%"?[0,100]:undefined}/>
                  <Tooltip content={<TT/>}/><Legend wrapperStyle={{fontSize:9,paddingTop:4}}/>
                  {TECHS.map(t=>(<Bar key={t.key} dataKey={t.key} name={t.label} stackId="a" fill={t.color}/>))}
                </BarChart>
              </ResponsiveContainer>
            </Card>
 
            <Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:2}}>
                <div><div style={{fontSize:11,fontWeight:700}}>EU Market by Segment</div><div style={{fontSize:9,color:C.muted}}>Source: BI EU market segmentation</div></div>
                <Toggle val={segView} onChange={setSegView} opts={["GW","%"]}/>
              </div>
              <ResponsiveContainer width="100%" height={CH}>
                <BarChart data={gSeg} margin={{top:4,right:8,left:0,bottom:0}}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                  <XAxis dataKey="year" stroke={C.muted} tick={xt} tickLine={false} tickFormatter={v=>v%5===0?v:""}/>
                  <YAxis stroke={C.muted} tick={yt} tickLine={false} width={YW} unit={segView==="GW"?" GW":"%"} domain={segView==="%"?[0,100]:[0,"auto"]} allowDataOverflow={false} tickFormatter={segView==="%"?v=>Math.round(v):undefined}/>
                  <Tooltip content={<TT/>}/><Legend wrapperStyle={{fontSize:8,paddingTop:4}}/>
                  {SEG_META.map(s=>(<Bar key={s.key} dataKey={s.key} name={s.label} stackId="seg" fill={s.color}/>))}
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
 
          {/* Bottom: CAPEX + Workforce */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:11}}>
            <Card>
              <div style={{fontSize:11,fontWeight:700,marginBottom:1}}>Capital Investment Required</div>
              <div style={{fontSize:9,color:C.muted,marginBottom:8}}>B€/yr — 5yr replacement, 2yr smoothing</div>
              <ResponsiveContainer width="100%" height={CHB}>
                <ComposedChart data={gCapexStack} margin={{top:4,right:8,left:0,bottom:0}}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                  <XAxis dataKey="year" stroke={C.muted} tick={xt} tickLine={false}/>
                  <YAxis stroke={C.muted} tick={yt} tickLine={false} width={YW} unit=" B€" domain={[0,d=>Math.ceil(Math.max(d,flt.capexHi*1.2))]} tickFormatter={v=>Math.round(v)}/>
                  <Tooltip content={<TT/>}/><Legend wrapperStyle={{fontSize:9,paddingTop:4}}/>
                  <ReferenceArea y1={flt.capexLo} y2={flt.capexHi} fill="#DC2626" fillOpacity={0.10} stroke="none" label={{value:`P${pw} filter: ${flt.capexLo}–${flt.capexHi} B€/yr`,fill:"#B91C1C",fontSize:8,position:"insideTopRight"}}/>
                  <Area dataKey="modCapex" stackId="c" name="Module" fill={SC.mod} stroke={SC.mod} fillOpacity={0.7} strokeWidth={0.5}/>
                  <Area dataKey="cellCapex" stackId="c" name="Cell" fill={SC.cell} stroke={SC.cell} fillOpacity={0.7} strokeWidth={0.5}/>
                  <Area dataKey="waferCapex" stackId="c" name="Wafer" fill={SC.wafer} stroke={SC.wafer} fillOpacity={0.7} strokeWidth={0.5}/>
                </ComposedChart>
              </ResponsiveContainer>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4,marginTop:6}}>
                {[["Total 2026–2050",`${totalCapex.toFixed(1)} B€`],["Peak",`${peakCapexRow.val.toFixed(2)} B€/yr (${peakCapexRow.year})`],["Wtd. cell CAPEX",`${wCapexCell.toFixed(0)} M€/GW`],["Wtd. mod CAPEX",`${wCapexMod.toFixed(0)} M€/GW`]].map(([l,v])=>(<div key={l} style={{display:"flex",justifyContent:"space-between",fontSize:9,padding:"2px 0",borderBottom:`1px solid ${C.border}`}}><span style={{color:C.label}}>{l}</span><span style={{color:C.text,fontWeight:600}}>{v}</span></div>))}
              </div>
              <div style={{fontSize:8,color:C.muted,marginTop:4,fontStyle:"italic"}}>VDMA (2026) × 1.5 EU × 2.5 total. 5yr replacement, 2yr smoothing. Real terms.</div>
            </Card>
 
            <Card>
              <div style={{fontSize:11,fontWeight:700,marginBottom:1}}>Workforce Requirements</div>
              <div style={{fontSize:9,color:C.muted,marginBottom:8}}>k FTE — operational</div>
              <ResponsiveContainer width="100%" height={CHB}>
                <ComposedChart data={gFteStack} margin={{top:4,right:8,left:0,bottom:0}}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                  <XAxis dataKey="year" stroke={C.muted} tick={xt} tickLine={false}/>
                  <YAxis stroke={C.muted} tick={yt} tickLine={false} width={YW} unit=" k" domain={[0,d=>Math.max(d,flt.fteHi*1.2)]}/>
                  <Tooltip content={<TT/>}/><Legend wrapperStyle={{fontSize:9,paddingTop:4}}/>
                  <ReferenceArea y1={flt.fteLo} y2={flt.fteHi} fill="#DC2626" fillOpacity={0.10} stroke="none" label={{value:`P${pw} filter: ${flt.fteLo}–${flt.fteHi}k`,fill:"#B91C1C",fontSize:8,position:"insideTopRight"}}/>
                  <Area dataKey="modFte" stackId="f" name="Module" fill={SC.mod} stroke={SC.mod} fillOpacity={0.7} strokeWidth={0.5}/>
                  <Area dataKey="cellFte" stackId="f" name="Cell" fill={SC.cell} stroke={SC.cell} fillOpacity={0.7} strokeWidth={0.5}/>
                  <Area dataKey="waferFte" stackId="f" name="Wafer" fill={SC.wafer} stroke={SC.wafer} fillOpacity={0.7} strokeWidth={0.5}/>
                </ComposedChart>
              </ResponsiveContainer>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4,marginTop:6}}>
                {[["At 2050",`${fte2050.toFixed(0)}k FTE`],["Peak",`${peakFteRow.val.toFixed(0)}k (${peakFteRow.year})`],["By 2030",`${((gFteStack.find(d=>d.year===2030)?.modFte??0)+(gFteStack.find(d=>d.year===2030)?.cellFte??0)+(gFteStack.find(d=>d.year===2030)?.waferFte??0)).toFixed(0)}k`],["IEA ref.",`~1,300 FTE/GW`]].map(([l,v])=>(<div key={l} style={{display:"flex",justifyContent:"space-between",fontSize:9,padding:"2px 0",borderBottom:`1px solid ${C.border}`}}><span style={{color:C.label}}>{l}</span><span style={{color:C.text,fontWeight:600}}>{v}</span></div>))}
              </div>
            </Card>
          </div>
 
          {/* ACTIONS GANTT */}
          <Card style={{borderTop:`2px solid ${pwColor}`}}>
            <div style={{marginBottom:8}}>
              <div style={{fontSize:11,fontWeight:700,color:pwColor}}>{PW[pw].label}: {PW[pw].name} — Policy Action Timeline</div>
              <div style={{fontSize:9,color:C.muted,fontStyle:"italic",marginTop:2}}>
                {pw===1?"Focused on the demand side. Implement existing and pipeline EU regulation."
                  :pw===2?"Add supply-side support. Shape the market for competitive European production."
                  :"Add protective measures. Insulate European production from external pressure."}
              </div>
              <div style={{fontSize:9,color:C.muted,marginTop:4}}>
                <span>■ Build-out</span><span style={{marginLeft:6,opacity:0.4}}>■ Maintenance/Transition</span>
              </div>
            </div>
            {(()=>{
              const gy=Array.from({length:25},(_,i)=>2026+i); // 2026-2050
              const tagColors={"D↑":"#3F8FBF","D↓":"#152A4A","I↑":"#E8902A","I↓":"#7B91B5"};
              const groups=[];
              for(let p=1;p<=pw;p++){
                groups.push({pw:p,label:PW[p].label,name:PW[p].name,color:PW[p].color,acts:PW_ACTIONS[p]||[],isCurrent:p===pw});
              }
              const HeaderRow=()=>(
                <div style={{display:"grid",gridTemplateColumns:"260px 1fr",gap:0,fontSize:9}}>
                  <div style={{borderBottom:`1px solid ${C.border}`,padding:"2px 4px",color:C.muted,fontWeight:700}}>Action</div>
                  <div style={{display:"grid",gridTemplateColumns:`repeat(${gy.length},1fr)`,borderBottom:`1px solid ${C.border}`}}>
                    {gy.map(y=>(<div key={y} style={{textAlign:"center",color:C.muted,padding:"2px 0",fontWeight:600,fontSize:7}}>{y%5===0||y===2026?y:""}</div>))}
                  </div>
                </div>
              );
              const ActionRows=({acts,groupColor})=>(
                <div style={{display:"grid",gridTemplateColumns:"260px 1fr",gap:0,fontSize:9}}>
                  {acts.map((a,ai)=>(
                    <React.Fragment key={ai}>
                      <div style={{padding:"3px 4px",borderBottom:`1px solid ${C.border}`,color:C.text,fontWeight:600,display:"flex",flexDirection:"column",gap:1,justifyContent:"center"}}>
                        <div style={{display:"flex",alignItems:"center",gap:4}}>
                          <span style={{fontSize:8,fontWeight:700,color:tagColors[a.tag]||C.muted,background:`${tagColors[a.tag]||C.muted}22`,padding:"1px 4px",borderRadius:3,whiteSpace:"nowrap"}}>{a.tag}</span>
                          <span style={{fontSize:9}}>{a.title}</span>
                        </div>
                        <span style={{fontSize:8,color:C.muted,fontWeight:400}}>{a.desc}</span>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:`repeat(${gy.length},1fr)`,borderBottom:`1px solid ${C.border}`,alignItems:"center",padding:"3px 0"}}>
                        {gy.map(y=>{
                          const active=y>=a.start&&y<=a.end;
                          const isMaint=a.phase==="maintain";
                          const isStart=y===a.start;
                          const isEnd=y===a.end;
                          return(<div key={y} style={{display:"flex",justifyContent:"center",height:12}}>
                            {active&&<div style={{width:"100%",height:8,
                              background:isMaint?`${groupColor}55`:`${groupColor}CC`,
                              borderRadius:isStart&&isEnd?4:isStart?"4px 0 0 4px":isEnd?"0 4px 4px 0":0,
                              margin:"2px 0"}}/>}
                          </div>);
                        })}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              );
              return(
                <div>
                  <HeaderRow/>
                  {groups.map(g=>(
                    <PwGanttGroup key={g.pw} group={g}>
                      <ActionRows acts={g.acts} groupColor={g.color}/>
                    </PwGanttGroup>
                  ))}
                </div>
              );
            })()}
            <div style={{display:"flex",gap:12,marginTop:8,fontSize:8,color:C.muted}}>
              {[["D↑","Direct upstream","#3F8FBF"],["D↓","Direct downstream","#152A4A"],["I↑","Indirect upstream","#E8902A"],["I↓","Indirect downstream","#7B91B5"]].map(([t,l,c])=>(
                <span key={t}><span style={{color:c,fontWeight:700}}>{t}</span> {l}</span>
              ))}
            </div>
          </Card>
 
        </div>
      </div>

       {/* FOOTER */}
      <div style={{background:"#E6EFF7",borderTop:`1px solid ${C.border}`,padding:"12px 24px",display:"flex",alignItems:"center",gap:14}}>
        <img src="/eu-funded-logo.png" alt="European Union" style={{height:50,objectFit:"contain",flexShrink:0}}/>
        <div style={{fontSize:12,color:C.muted,lineHeight:1.4}}>
          This project has received funding from the European Union's Horizon Europe research and innovation programme under grant agreement No.101084259
        </div>
      </div>

    </div>
  );
}